import { useEffect } from 'react';
import { useLanguage, Language, addLangPrefix, stripLangPrefix } from '../contexts/LanguageContext';

export interface SEOProps {
  title: string;
  description?: string;
  /** 'index,follow' | 'noindex,nofollow' */
  robots?: string;
  /**
   * Bare path WITHOUT a language prefix, e.g. '/faq' or '/blog/delays-in-dr'.
   * useSEO prepends the correct locale prefix automatically.
   * Pass a full https:// URL only when you need to override completely.
   */
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  /** Optional Schema.org JSON-LD object */
  schemaOrg?: Record<string, unknown>;
}

const SITE_NAME        = 'HipoTech';
const BASE_URL         = 'https://hipotech.net';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-default.png`;

const LOCALE_MAP: Record<Language, string> = {
  en: 'en_US',
  es: 'es_DO',
  fr: 'fr_FR',
};

export function useSEO({
  title,
  description,
  robots = 'index,follow',
  canonical,
  ogTitle,
  ogDescription,
  ogType  = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  schemaOrg,
}: SEOProps) {
  const { language } = useLanguage();

  // ── Synchronous SEO (react-snap compatibility) ────────────────────────
  // react-snap captures the DOM after render but BEFORE useEffect fires.
  // Setting title and critical meta tags here ensures they're in the HTML
  // snapshot that gets served to crawlers. The useEffect below handles
  // dynamic updates (language switches, route changes) for live users.
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  if (typeof document !== 'undefined') {
    document.title = fullTitle;
    document.documentElement.lang = language;

    const setMetaSync = (attr: 'name' | 'property', key: string, content: string) => {
      const selector = `meta[${attr}="${key}"]`;
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) setMetaSync('name', 'description', description);
    setMetaSync('name', 'robots', robots);
    setMetaSync('property', 'og:title', ogTitle ?? fullTitle);
    setMetaSync('name', 'twitter:title', ogTitle ?? fullTitle);
    if (description) {
      setMetaSync('property', 'og:description', ogDescription ?? description);
      setMetaSync('name', 'twitter:description', ogDescription ?? description);
    }

    // Schema.org JSON-LD — inject synchronously so react-snap captures it
    if (schemaOrg) {
      const SCHEMA_ID = 'ld-json-schema';
      let schemaEl = document.getElementById(SCHEMA_ID) as HTMLScriptElement | null;
      if (!schemaEl) {
        schemaEl = document.createElement('script');
        schemaEl.id = SCHEMA_ID;
        schemaEl.type = 'application/ld+json';
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schemaOrg);
    }
  }

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = fullTitle;

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      const selector = `meta[${attr}="${key}"]`;
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    if (description) setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);

    // ── Canonical & hreflang ──────────────────────────────────────────────
    // canonical is stored as a bare path in seoConfig (e.g. '/faq').
    // We build locale-prefixed full URLs for each language variant.
    if (canonical) {
      // Normalise: strip any accidental full URL or existing lang prefix
      const barePath = canonical.startsWith('http')
        ? (canonical.replace(BASE_URL, '') || '/')
        : stripLangPrefix(canonical);

      const suffix   = barePath === '/' ? '' : barePath;
      const enUrl    = `${BASE_URL}${barePath}`;          // English — no prefix
      const esUrl    = `${BASE_URL}/es${suffix}`;
      const frUrl    = `${BASE_URL}/fr${suffix}`;
      const thisUrl  = language === 'en' ? enUrl
                     : language === 'es' ? esUrl
                     : frUrl;

      // Canonical points to this language's URL
      setLink('canonical', thisUrl);
      setMeta('property', 'og:url', thisUrl);

      // hreflang — every locale gets its own distinct URL
      setLink('alternate', enUrl, 'en');
      setLink('alternate', esUrl, 'es');
      setLink('alternate', frUrl, 'fr');
      setLink('alternate', enUrl, 'x-default');  // x-default → English
    }

    // ── Open Graph ────────────────────────────────────────────────────────
    setMeta('property', 'og:title',     ogTitle ?? fullTitle);
    setMeta('property', 'og:type',      ogType);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale',    LOCALE_MAP[language]);
    if (description) setMeta('property', 'og:description', ogDescription ?? description);
    setMeta('property', 'og:image', ogImage);

    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    (['en', 'es', 'fr'] as Language[])
      .filter(l => l !== language)
      .forEach(alt => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'og:locale:alternate');
        el.setAttribute('content', LOCALE_MAP[alt]);
        document.head.appendChild(el);
      });

    // ── Twitter ───────────────────────────────────────────────────────────
    setMeta('name', 'twitter:card',  'summary_large_image');
    setMeta('name', 'twitter:title', ogTitle ?? fullTitle);
    if (description) setMeta('name', 'twitter:description', ogDescription ?? description);
    setMeta('name', 'twitter:image', ogImage);

    // ── Schema.org JSON-LD ────────────────────────────────────────────────
    const SCHEMA_ID = 'ld-json-schema';
    let schemaEl = document.getElementById(SCHEMA_ID) as HTMLScriptElement | null;
    if (schemaOrg) {
      if (!schemaEl) {
        schemaEl = document.createElement('script');
        schemaEl.id   = SCHEMA_ID;
        schemaEl.type = 'application/ld+json';
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schemaOrg);
    } else if (schemaEl) {
      schemaEl.remove();
    }
  }, [title, description, robots, canonical, ogTitle, ogDescription, ogType, ogImage, language, schemaOrg]);
}

export function useNoIndex(title: string) {
  useSEO({ title, robots: 'noindex,nofollow' });
}